import { TranslationResult, APIResponse } from '../types';
import { getLanguageByCode } from '../data/languages';
import { supabase } from '../lib/supabase';

// Cache for API keys to minimize database queries
let cachedKeys: { [key: string]: string } = {};

// Fetch API keys from Supabase
async function getApiKey(provider: 'google' | 'openai'): Promise<string> {
  if (cachedKeys[provider]) {
    return cachedKeys[provider];
  }

  const { data, error } = await supabase
    .from('api_keys')
    .select('key')
    .eq('provider', provider)
    .eq('is_valid', true)
    .single();

  if (error || !data) {
    throw new Error(`Failed to fetch ${provider} API key`);
  }

  cachedKeys[provider] = data.key;
  return data.key;
}

// Rate limiting configuration
const rateLimiter = new Map<string, { count: number; timestamp: number }>();
const RATE_LIMIT = 100; // requests per window
const RATE_WINDOW = 15 * 60 * 1000; // 15 minutes in milliseconds

const checkRateLimit = (clientId: string): boolean => {
  const now = Date.now();
  const clientData = rateLimiter.get(clientId);
  
  if (!clientData) {
    rateLimiter.set(clientId, { count: 1, timestamp: now });
    return true;
  }
  
  if (now - clientData.timestamp > RATE_WINDOW) {
    rateLimiter.set(clientId, { count: 1, timestamp: now });
    return true;
  }
  
  if (clientData.count >= RATE_LIMIT) {
    return false;
  }
  
  clientData.count += 1;
  rateLimiter.set(clientId, clientData);
  return true;
};

// Google Cloud Translation API implementation
async function translateWithGoogle(
  text: string,
  sourceLanguage: string,
  targetLanguage: string
): Promise<string> {
  const apiKey = await getApiKey('google');
  const response = await fetch(
    `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        source: sourceLanguage === 'auto' ? undefined : sourceLanguage,
        target: targetLanguage,
        format: 'text',
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Google translation failed');
  }

  const data = await response.json();
  return data.data.translations[0].translatedText;
}

// OpenAI enhancement implementation
async function enhanceWithOpenAI(
  originalText: string,
  translatedText: string,
  targetLanguage: string
): Promise<string> {
  const apiKey = await getApiKey('openai');
  
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'gpt-4-turbo-preview',
      messages: [
        {
          role: 'system',
          content: `You are a professional translator. Review and enhance the machine translation while preserving the original meaning. Target language: ${targetLanguage}`
        },
        {
          role: 'user',
          content: `Original text: "${originalText}"\nMachine translation: "${translatedText}"\n\nPlease review and enhance this translation if necessary, maintaining natural language flow and cultural context. Return ONLY the enhanced translation, without any explanations or additional text.`
        }
      ],
      temperature: 0.3,
      max_tokens: 150
    })
  });

  if (!response.ok) {
    // If OpenAI enhancement fails, return the original Google translation
    return translatedText;
  }

  const data = await response.json();
  return data.choices[0].message.content.trim();
}

export const translateText = async (
  text: string,
  sourceLanguage: string,
  targetLanguage: string,
  clientId: string = 'default'
): Promise<TranslationResult> => {
  if (!checkRateLimit(clientId)) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }
  
  if (!text?.trim()) {
    throw new Error('Translation text cannot be empty');
  }

  if (!sourceLanguage || !targetLanguage) {
    throw new Error('Source and target languages must be specified');
  }

  try {
    // First, get the base translation from Google
    const baseTranslation = await translateWithGoogle(text, sourceLanguage, targetLanguage);

    // Determine if AI enhancement is needed
    const needsEnhancement = text.length > 10 && // Only enhance longer texts
      !/^\d+$/.test(text) && // Skip pure numbers
      !/^[!@#$%^&*(),.?":{}|<>]+$/.test(text); // Skip pure punctuation

    // If enhancement is needed, process with OpenAI
    const finalTranslation = needsEnhancement
      ? await enhanceWithOpenAI(text, baseTranslation, targetLanguage)
      : baseTranslation;

    return {
      sourceText: text,
      translatedText: finalTranslation,
      sourceLanguage,
      targetLanguage,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('Translation error:', error);
    throw error;
  }
};

export const detectLanguage = async (text: string): Promise<string> => {
  if (!text?.trim()) {
    throw new Error('Text cannot be empty');
  }

  try {
    const apiKey = await getApiKey('google');
    const response = await fetch(
      `https://translation.googleapis.com/language/translate/v2/detect?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: text
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Language detection failed');
    }

    const data = await response.json();
    return data.data.detections[0][0].language;
  } catch (error) {
    console.error('Language detection error:', error);
    return 'en'; // Fallback to English
  }
};

export const speakText = (text: string, languageCode: string): void => {
  if (!('speechSynthesis' in window)) {
    console.error('Speech synthesis not supported');
    return;
  }
  
  if (!text?.trim()) {
    console.error('Text cannot be empty');
    return;
  }
  
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = languageCode;
  window.speechSynthesis.speak(utterance);
};